/**
 * O registry PARCIAL do CRM — spec-010 do `project-plataforma-por-chat`,
 * [T014] / US-1.
 *
 * O que o CRM já desenhava na tela (kanban de oportunidades, atividades
 * sugeridas, caixa de e-mails, conversas de WhatsApp, prioridades de
 * relacionamento) passa a ser desenhável DENTRO da conversa, sem que o
 * `chat-message.vue` saiba que o CRM existe: ele
 * resolve a chave que o backend mandou (`ui.component`) no `UI_REGISTRY` e
 * monta. Cartão novo do módulo é uma entrada aqui — nunca uma linha na bolha.
 *
 * ## Como cada peça se encaixa
 *
 * | peça | onde mora | por quê |
 * |---|---|---|
 * | `component` | `ia/cartoes/*.js` | embrulho que traduz o `ui.props` em props |
 * | `schema` | `ui-registry/schemas/` | CÓPIA byte a byte do `contratos/` do backend |
 * | ação de abrir | `ia/acoes.js` | modal e rota se resolvem AQUI (D8), não pelo `respond` |
 *
 * ★ O `component` NÃO aponta para o `.vue`. Aponta para o embrulho, porque o
 * fio é snake_case (`valor_brl`, `data_prevista_fechamento`) e os componentes
 * reusados falam o vocabulário deles — o do kanban, no caso da oportunidade.
 * Apontar direto deixaria o cartão montado e VAZIO, que é a falha calada que
 * este contrato existe para evitar.
 *
 * ★ O `schema` valida o `ui.props` CRU (é ele que o `chat-message` passa a
 * `validarProps`), e por isso é o contrato do backend sem uma vírgula de
 * diferença. A junção `ui-registry/contrato-ui-schemas.unit.js` compara os dois
 * lados byte a byte.
 *
 * ## O `label` é o do BACKEND, não o título do cartão
 *
 * `label` é o que o `tool-call-badge` mostra ENQUANTO a tool roda ("Consultando
 * funil de oportunidades..."), e por isso ele é byte a byte o `LABEL` da tool
 * emissora (D10). O substantivo curto ("Funil de oportunidades") é o TÍTULO do
 * cartão e vive no `.vue`: são dois textos, em dois momentos, e juntá-los faria
 * o badge anunciar um substantivo solto ou o cartão se apresentar como uma
 * frase de espera.
 *
 * `registry.unit.js` compara cada `label` com o arquivo `.py` da tool: rótulo
 * divergente não quebra nada — o badge diz uma coisa e o thread outra.
 *
 * ## Duas decisões que valem explicar
 *
 * 1. **A flag é a do MÓDULO (`FEATURES.CRM`), não a do kanban.** A tela que
 *    mostra este dado é `crm.opportunities`, e ela é gateada por `FEATURES.CRM`
 *    (`modules/crm/routes.js`) — a flag `FEATURES.OPPORTUNITIES` pertence a
 *    OUTRO módulo (ofertas/alocação). Gatear por ela esconderia o cartão de
 *    quem tem o CRM, com o `fallback-card` dizendo `flag_desligada` no meio de
 *    uma conversa que a pessoa conseguiu ter.
 * 2. **`states` são os dois de leitura.** `input-available` entra porque é o
 *    estado em que a parte vive enquanto a tool roda: recusá-lo faria aparecer
 *    um cartão de ERRO (`estado_nao_suportado`) no meio de todo turno normal,
 *    trocado pelo cartão certo um segundo depois. Os desfechos de erro ficam de
 *    fora: nenhum dos dois cartões sabe desenhar uma falha, e um cartão que
 *    renderiza nada é pior que o `fallback-card` dizendo o que aconteceu.
 *
 * ⚠️ **O que NÃO entra aqui: o `LEGACY_TOOL_TO_UI`.** As duas tools declaram
 * `ui` no backend desde a [T002]/[T003], então a parte já chega com a chave.
 * Mapear `get_funil_vendas`/`list_oportunidades` no mapa legado faria as
 * mensagens ANTIGAS (persistidas antes desta spec, sem `ui`) resolverem para
 * estes cartões com o `value` da tool no lugar do `ui.props` — outro shape
 * (`estagios`, não `etapas`) —, e a conversa reaberta trocaria o silêncio de
 * hoje por um `fallback-card` de contrato reprovado.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */
import { FEATURES } from '@constants/Text'
import { ROUTE_KEYS } from '@constants/navigation-routes'

export { ROTA_DO_KANBAN } from './acoes'

/** A chave do módulo no catálogo de navegação (`ROUTE_KEYS.CRM`). */
const MODULO = ROUTE_KEYS.CRM

/** A flag do módulo — ver a decisão 1 no cabeçalho. */
const FLAG_DO_MODULO = FEATURES.CRM

// Enquanto a tool roda, e quando o resultado chega.
const ESTADOS_DE_LEITURA = ['input-available', 'output-available']

/**
 * As chaves, no formato `<modulo>/<cartao>` do contrato.
 *
 * Exportadas porque quem lê o registry e quem escreve o teste precisam da MESMA
 * string: uma chave redigitada não quebra nada visivelmente — o cartão
 * simplesmente não aparece.
 */
export const CHAVES = {
  FUNIL: 'crm/funil',
  FUNIL_LEADS: 'crm/funil-leads',
  OPORTUNIDADES: 'crm/oportunidades',
  ATIVIDADES: 'crm/atividades-sugeridas',
  EMAILS: 'crm/emails',
  WHATSAPP: 'crm/conversas-whatsapp',
  CLIENTES_PRIORITARIOS: 'crm/clientes-prioritarios',
  LEADS_PRIORIDADE: 'crm/leads-prioridade',
}

export default {
  [CHAVES.FUNIL]: {
    component: () => import('@modules/crm/ia/cartoes/funil'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-crm-funil.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    states: ESTADOS_DE_LEITURA,
    label: 'Consultando funil de oportunidades...',
  },
  [CHAVES.FUNIL_LEADS]: {
    component: () => import('@modules/crm/ia/cartoes/funil-leads'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-crm-funil-leads.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    states: ESTADOS_DE_LEITURA,
    label: 'Consultando funil de leads...',
  },
  [CHAVES.OPORTUNIDADES]: {
    component: () => import('@modules/crm/ia/cartoes/oportunidades'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-crm-oportunidades.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    states: ESTADOS_DE_LEITURA,
    label: 'Consultando oportunidades...',
  },
  [CHAVES.ATIVIDADES]: {
    component: () => import('@modules/crm/ia/cartoes/atividades-sugeridas'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-crm-atividades-sugeridas.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    states: ESTADOS_DE_LEITURA,
    label: 'Consultando atividades sugeridas...',
  },
  [CHAVES.EMAILS]: {
    component: () => import('@modules/crm/ia/cartoes/emails'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-crm-emails.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    states: ESTADOS_DE_LEITURA,
    label: 'Consultando e-mails...',
  },
  [CHAVES.WHATSAPP]: {
    component: () => import('@modules/crm/ia/cartoes/conversas-whatsapp'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-crm-conversas-whatsapp.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    states: ESTADOS_DE_LEITURA,
    label: 'Consultando conversas de WhatsApp...',
  },
  [CHAVES.CLIENTES_PRIORITARIOS]: {
    component: () => import('@modules/crm/ia/cartoes/clientes-prioritarios'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-crm-clientes-prioritarios.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    states: ESTADOS_DE_LEITURA,
    label: 'Consultando clientes prioritários...',
  },
  [CHAVES.LEADS_PRIORIDADE]: {
    component: () => import('@modules/crm/ia/cartoes/leads-prioridade'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-crm-leads-prioridade.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    states: ESTADOS_DE_LEITURA,
    label: 'Consultando leads prioritários...',
  },
}
